// utils/adminUrlGenerator.js

// Definição de mapeamento de rotas
const adminRoutes = {
    // academy
    institution: () => `/academy/institutions`,
    // Emitted by NewCreatorApplicationNotification when a coach/mentor
    // submits their profile for review. Its own data.url is currently
    // hardcoded to null (fixed alongside this in academy-api), so every
    // notification persisted before that ships still needs this fallback
    // - without it the bell click is a silent dead end, same as every
    // other entry in this file that carries that comment.
    academy_creator: (id) =>
        id ? `/academy/creator-applications/detail/${id}` : `/academy/creator-applications/list`,

    // agency
    brand: () => `/agency/marketing/brands/list`,
    mundo_indie_request: () => `/agency/mundo-indie/projects/list`,
    showcase_submission: () => `/agency/showcases-submissions/list`,
    showcase_media_partner_application: (id) =>
        id
            ? `/agency/showcase-media-partner-applications/detail/${id}`
            : `/agency/showcase-media-partner-applications/list`,
    showcase_sponsor_application: () => `/agency/showcase-sponsor-applications/list`,
    showcase_coverage_submission: () => `/agency/showcase-coverage-submissions/list`,
    // Emitted by agency-api's Video Distribution flow. Without this entry
    // generateAdminRoute() returns null and clicking the bell goes nowhere.
    video_submission: id =>
        (id
            ? `/agency/video-submissions/list?highlight=${id}`
            : `/agency/video-submissions/list`),
    showcase_interview_booking: () => `/agency/showcases-submissions/list`,
    // Emitted by agency-api's showcase deliverables flow (materials delivered,
    // extension requested, trailer duration unverifiable...). data.id is the
    // submission id. Without this entry generateAdminRoute() returns null and
    // the bell click is a silent dead end.
    showcase_deliverable: (id) =>
        id
            ? `/agency/showcase-deliverables/detail/${id}`
            : `/agency/showcases-submissions/list`,
    agency_service_request: () => `/agency/requests/list`,
    campaign: () => `/agency/marketing/campaigns-banners/list`,
    campaign_order: (id) => id ? `/agency/campaign-orders/${id}` : `/agency/campaign-orders/list`,
    proposal: () => `/agency/proposals/list`,
    key: () => `/agency/keys/keys/list`,
    key_requests: (id) => id ? `/agency/keys/requests/list?id=${id}` : `/agency/keys/requests/list`,
    key_material: (id) => id ? `/agency/keys/materials?id=${id}` : `/agency/keys/materials`,
    showcase_prospects_sync: () => `/agency/studio-prospects/board`,
    influencer_registration: (id) =>
        id ? `/agency/influencers/edit/${id}` : `/agency/influencers/list`,
    journalist_registration: () => `/agency/journalists/list`,
    podcast_episode: () => `/agency/podcasts/episodes/list`,
    // Emitted by PodcastGuestSelfSubmissionNotification when a guest
    // self-submits. Without this entry generateAdminRoute() returns null
    // and the bell click is a silent dead end.
    podcast_guest: (id) =>
        id ? `/agency/podcasts/guests/edit/${id}` : `/agency/podcasts/guests/list`,
    // Emitted by PodcastSponsorSlotSelfSubmissionNotification when a sponsor
    // self-submits. The "Sponsors" admin screen (despite its name) edits
    // PodcastSponsorSlot records — it calls fetchSponsor() -> GET
    // /podcast-sponsor-slots/{id} under the hood — so this id maps straight
    // to it. Without this entry generateAdminRoute() returns null and the
    // bell click is a silent dead end.
    podcast_sponsor_slot: (id) =>
        id ? `/agency/podcasts/sponsors/edit/${id}` : `/agency/podcasts/sponsors/list`,
    press_kit: (id) => id ? `/agency/press-kits/${id}` : `/agency/press-kits/list`,
    media_kit: (id) => id ? `/agency/media-kits/${id}` : `/agency/media-kits/list`,
    // Emitted by StudioOnboardingSubmittedNotification. That notification's
    // own data.url already covers this (AppBarNotification.vue now prefers
    // it), but keeping an entry here too is what protects any notification
    // persisted before the url field existed.
    studio_onboarding_submission: (id) =>
        id ? `/agency/studio-onboarding/submissions?submission_id=${id}` : `/agency/studio-onboarding/submissions`,

    // community
    rating_commentary: () => `/community/ratings`,
    content_commentary: () => `/community/comments`,
    report: () => `/community/reports/list`,
    game_claim: () => `/community/games/claims`,
    game_contribution: () => `/community/games/contributions`,
    content_request: () => `/community/content-requests`,
    // Split by content type so a click opens the specific item instead of a
    // generic list (the list only ever showed Pulse rows anyway, so review/video
    // notifications used to land on a page that couldn't show them at all).
    editorial_moderation_pulse: id => (id ? `/community/contents/pulses/edit/${id}` : `/community/pulses/pending`),
    editorial_moderation_review: id => (id ? `/community/contents/reviews/edit/${id}` : `/community/pulses/pending`),
    editorial_moderation_video: id => (id ? `/community/contents/videos/edit/${id}` : `/community/pulses/pending`),
    // Fallback for notifications already persisted before this split shipped.
    editorial_moderation: () => `/community/pulses/pending`,

    // jobs
    vacancy: (id) => id ? `/vacancies/edit/${id}` : `/vacancies/list`,
    salary_submission: () => `/salary-submissions/list`,
    mentorship_recruitment_request: () => `/requests/list`,
    // Emitted by jobs-api's GigListingCreatedNotification when a professional
    // publishes a new Gig service — that notification's own data.url is
    // hardcoded null (no admin deep-link config existed for it), so without
    // this entry generateAdminRoute() returns null and the bell click is a
    // silent dead end, same failure mode as every other entry in this file
    // carrying this comment. gig-listings/list.vue shows every status by
    // default (no default filter to work around), so ?highlight= only
    // matches this file's own convention — it isn't consumed there yet,
    // same as most ?highlight= entries above.
    gig_listing: (id) =>
        id ? `/jobs/gig-listings/list?highlight=${id}` : `/jobs/gig-listings/list`,

    // network
    order: () => `/network/financial/invoices/list`,
    payment_failed: () => `/network/financial/invoices/list`,
    new_invoice: () => `/network/financial/invoices/list`,
    invoice: () => `/network/financial/invoices/list`,
    planSubscription: () => `/network/subscriptions`,
    contact_message: () => `/mundo-gamer-platforms/messages`,
    career_application: () => `/network/careers/applications/list`,
    skill: () => `/network/careers/skills/list`,
    company: (id) => id ? `/companies/edit/${id}` : `/companies/list`,
    // Emitted by CompanyClaimRequested/Approved/Rejected (api-main). Had no
    // matching admin screen at all until this entry — the notification's own
    // hardcoded url pointed at a route that never existed. See
    // src/views/companies/company-claims/list.vue.
    company_claim: () => `/companies/claims`,
    newsletter_subscription: () => `/mundo-gamer-newsletters/0`,
    // Emitted by NewPartnerMessageNotification (api-main) when a new
    // partnership inquiry comes in. That notification's own data.url is
    // hardcoded null, so without this entry generateAdminRoute() returns
    // null and the bell click is a silent dead end. id is the Partner id.
    partner_message: (id) =>
        id ? `/network/partners/edit/${id}` : `/network/partners/list`,
    // Emitted by DeletionRequestCreated/Approved (api-main) for a user's
    // request to delete a company or game they manage. Both notifications'
    // own data.url are hardcoded null; the list has status tabs, not a
    // per-id route, so this always lands there regardless of id.
    deletion_request: () => `/network/deletion-requests`,
    // Sent by the overdue-material auto-block alert, whose whole purpose is to
    // put an admin on that creator's profile — where the reason and the
    // unblock button live. Without an entry here the click did nothing.
    user: id => (id ? `/network/users/edit/${id}` : `/network/users/list`),
    user_testimonial: () => `/agency/user-testimonials/list`,
    wall_block: () => `/network/wall/blocks/list`,
    affiliateDocument: () => `/network/financial/affiliates/documents/list`,
    affiliateCommissionPaymentRequest: () => `/network/financial/affiliates/requests/list`,
    // Rows from the unified fulfillment queue (/operations/fulfillment-queue,
    // api-main's app/Shared/FulfillmentQueue). Deliberately new keys, not a
    // reuse of the existing `campaign_order` entry above — that one is keyed
    // by CampaignOrder id from the notification system, a different id
    // semantics than this queue's campaign_order_item id.
    // Verified against src/router/agency/phase9.js — no ?highlight= support,
    // so this opens the order, not the specific item within it.
    fulfillment_campaign_order_item: (orderId) =>
        orderId ? `/agency/campaign-orders/${orderId}` : `/agency/campaign-orders/list`,
    // Verified against src/router/agency/showcase.js — list has no
    // ?highlight=/per-id route yet, so this always lands on the list.
    fulfillment_showcase_submission: () => `/agency/showcases-submissions/list`,
    // Verified against src/router/community/content-requests.js — ContentRequest
    // has no addressable per-id route (its "detail" is an in-page dialog), so
    // this always lands on the list too.
    fulfillment_review_request: () => `/community/content-requests`,

    // tv
    streamer_application: () => `/tv/streamers/applications/list`,
    streamer: () => `/tv/streamers/list`,
    indie_wall_payout: () => `/tv/walls/payouts`,
    indie_wall: () => `/tv/walls/list`,

    // ventures
    waitlist_registration: () => `/ventures/waitlist`,

    // support
    // Emitted when a new support ticket is created/updated (see
    // SupportTicketController on the backend). data.id is the ticket uuid.
    // That notification's own data.url should already cover this
    // (AppBarNotification.vue prefers it), but keeping an entry here too
    // protects any notification persisted before the url field existed —
    // same rationale as studio_onboarding_submission above.
    support_ticket: (id) =>
        id ? `/support-tickets/${id}` : `/support-tickets`,
};

/**
 * Gera a rota com base no tipo de objeto e ID.
 * @param {string} object - Tipo do objeto (e.g., 'vacancy', 'user').
 * @param {number|string} id - ID do objeto.
 * @returns {string|null} - A rota gerada ou `null` se o tipo for desconhecido.
 */
export function generateAdminRoute(object, id) {
    const route = adminRoutes[object];
    if (!route) {
        console.warn(`Tipo de objeto desconhecido: ${object}`);
        return null;
    }
    return route(id);
}
